import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { DAY_AXIS_CHART_MARGIN, getChartTokens, yAxisWidth } from '../../theme/chartTheme';
import type { DailyWasteAmount } from '../../types/dashboard.type';
import { formatDateTime } from '../../utils/numberFormat';
import { localDateKey } from '../../utils/serverTime';

export type DailyChartType = 'bar' | 'line' | 'area';

interface DailyPoint {
  date: string;
  kg: number;
}

/**
 * Sum per day (the API splits by waste type; today it sends "all"). For a
 * month, days without orders are shown as 0 — up to today, never in the future.
 */
export function buildDailySeries(rows: readonly DailyWasteAmount[], monthStart: Date | null): DailyPoint[] {
  const byDate = new Map<string, number>();
  for (const row of rows) {
    const key = (row.date ?? '').slice(0, 10);
    if (!key || !Number.isFinite(row.amount)) continue;
    byDate.set(key, (byDate.get(key) ?? 0) + row.amount);
  }
  if (monthStart) {
    const today = localDateKey(new Date());
    const cursor = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
    const month = cursor.getMonth();
    while (cursor.getMonth() === month) {
      const key = localDateKey(cursor);
      if (key > today) break;
      if (!byDate.has(key)) byDate.set(key, 0);
      cursor.setDate(cursor.getDate() + 1);
    }
  }
  return [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, kg]) => ({ date, kg }));
}

interface DailyRequestsChartProps {
  rows: DailyWasteAmount[];
  /** First day of the displayed month; null for the lifetime view. */
  monthStart: Date | null;
  type: DailyChartType;
  height?: number;
  ariaLabel: string;
}

const DailyRequestsChart: React.FC<DailyRequestsChartProps> = ({ rows, monthStart, type, height = 260, ariaLabel }) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const fmt = useNumberFormat();
  const tokens = useMemo(() => getChartTokens(theme), [theme]);
  const data = useMemo(() => buildDailySeries(rows, monthStart), [rows, monthStart]);
  const color = theme.palette.primary.main;
  const dayLabel = (value: string) =>
    formatDateTime(`${value}T00:00:00`, i18n.language, { day: 'numeric', month: 'short' });

  const axes = (
    <>
      <CartesianGrid stroke={tokens.grid.stroke} vertical={false} />
      <XAxis
        dataKey="date"
        tickFormatter={dayLabel}
        tick={tokens.axis.tick}
        axisLine={false}
        tickLine={false}
        tickMargin={8}
        minTickGap={18}
      />
      <YAxis
        tickFormatter={(v: number) => fmt.compact(v)}
        tick={tokens.axis.tick}
        axisLine={false}
        tickLine={false}
        width={yAxisWidth(fmt.compact(Math.max(0, ...data.map((d) => d.kg))))}
      />
      <Tooltip
        cursor={tokens.tooltip.cursor}
        contentStyle={tokens.tooltip.contentStyle}
        labelStyle={tokens.tooltip.labelStyle}
        itemStyle={tokens.tooltip.itemStyle}
        labelFormatter={(v: string) => dayLabel(v)}
        formatter={(v: number) => [fmt.withUnit('mass_kg', v), '']}
        separator=""
      />
    </>
  );

  return (
    // Time runs left to right in every language; the surrounding text stays RTL.
    <Box dir="ltr" role="img" aria-label={ariaLabel} sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {type === 'line' ? (
          <LineChart data={data} margin={DAY_AXIS_CHART_MARGIN}>
            {axes}
            <Line type="monotone" dataKey="kg" stroke={color} strokeWidth={2} dot={false} activeDot={tokens.line.activeDot} {...tokens.animation} />
          </LineChart>
        ) : type === 'area' ? (
          <AreaChart data={data} margin={DAY_AXIS_CHART_MARGIN}>
            {axes}
            <Area
              type="monotone"
              dataKey="kg"
              stroke={color}
              strokeWidth={2}
              fill={color}
              fillOpacity={0.12}
              activeDot={tokens.line.activeDot}
              {...tokens.animation}
            />
          </AreaChart>
        ) : (
          <BarChart data={data} margin={DAY_AXIS_CHART_MARGIN}>
            {axes}
            <Bar dataKey="kg" fill={color} maxBarSize={tokens.bar.maxBarSize} radius={tokens.bar.radius} {...tokens.animation} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};

export default DailyRequestsChart;
